import { C as Cart } from './cart-DZS7CGyq.js';
import { P as PubSub } from './pubsub-COBwjfLh.js';
import { N as Notyf } from './notyf-DIw0RvBj.js';
import './uniq-BDEfcc0N.js';

/**
 * Cart drawer FBT sync: quick-add, list refresh, restore, and clear.
 */


const RECOMMEND_MINI_CART_SOURCE = 'algolia-recommend-mini-cart';
const GWP_EVENT_SOURCE = 'cart-gifts-with-purchase';
const GWP_SYNC_SOURCE = 'cart-gifts-sync';
const CART_CLEAR_EVENTS = ['cart:clear', 'cart:change', 'cart:update'];

class RecommendCartDrawerSync {
  #drawerObserver;

  #onRecommendClick = (event) => {
    const button = event.target.closest('[data-recommend-add]');
    if (!button?.closest('algolia-recommend-mini-cart')) return;

    event.preventDefault();
    event.stopPropagation();
    void this.#addRecommendation(button);
  };

  constructor() {
    document.addEventListener('click', this.#onRecommendClick);

    PubSub.subscribe('cart:add', (payload) => this.#onCartAdd(payload));
    CART_CLEAR_EVENTS.forEach((eventName) => {
      PubSub.subscribe(eventName, (payload) => this.#onCartClear(eventName, payload));
    });
    PubSub.subscribe('dialog:open', (payload) => this.#onDialogOpen(payload));

    this.#observeCartDrawer();
  }

  /**
   * @param {Element} element
   * @param {string} name
   */
  #readAlgoliaDataAttr(element, name) {
    return element.getAttribute(name)?.trim() || undefined;
  }

  /** @param {string | null} raw */
  #parseAlgoliaInsightsPosition(raw) {
    const position = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(position) && position > 0 ? position : undefined;
  }

  /** @param {Element} card */
  #getAlgoliaRecommendClickContext(card) {
    const queryID = this.#readAlgoliaDataAttr(card, 'data-algolia-query-id');
    const objectID = this.#readAlgoliaDataAttr(card, 'data-algolia-object-id');
    const index =
      this.#readAlgoliaDataAttr(card, 'data-algolia-index') ||
      window.AlgoliaInsights?.getProductsIndex?.();

    if (!queryID || !objectID || !index) return null;

    return {
      hit: {
        objectID,
        queryID,
        productId: this.#readAlgoliaDataAttr(card, 'data-algolia-product-id'),
      },
      options: {
        index,
        eventName: this.#readAlgoliaDataAttr(card, 'data-algolia-event-name') || 'Product Clicked',
        position: this.#parseAlgoliaInsightsPosition(card.getAttribute('data-algolia-position')),
      },
    };
  }

  /**
   * Record Algolia click attribution before quick-add so Search Converted and
   * pixel AfterSearch conversion events can link to the Recommend queryID.
   *
   * @param {HTMLButtonElement} button
   */
  #trackAlgoliaRecommendClickForConversion(button) {
    const card = button.closest('[data-algolia-object-id]');
    if (!card) return;

    const trackSearchAttribution = window.AlgoliaInsights?.trackSearchAttribution;
    if (typeof trackSearchAttribution !== 'function') return;

    const context = this.#getAlgoliaRecommendClickContext(card);
    if (!context) return;

    const { hit, options } = context;
    trackSearchAttribution(
      {
        objectID: hit.objectID,
        queryID: hit.queryID,
        ...(hit.productId ? { productId: hit.productId } : {}),
      },
      {
        index: options.index,
        eventName: options.eventName,
        ...(options.position ? { position: options.position } : {}),
      }
    );
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #addRecommendation(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.classList.contains('loading')) return;

    button.classList.add('loading');
    button.setAttribute('disabled', 'true');
    button.ariaBusy = true;

    try {
      this.#trackAlgoliaRecommendClickForConversion(button);

      await Cart.add({
        items: [{ id: variantId, quantity: 1 }],
        event_data: { source: RECOMMEND_MINI_CART_SOURCE },
      });
    } catch (error) {
      Notyf.error(error?.message || window.toast_translations.cart.error);
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('disabled');
      button.ariaBusy = false;
    }
  }

  #forEachMiniCart(callback) {
    document.querySelectorAll('algolia-recommend-mini-cart').forEach((element) => {
      callback(element);
    });
  }

  #restoreRecommendations() {
    this.#forEachMiniCart((element) => {
      element.restoreRecommendations?.();
    });
  }

  #clearRecommendations() {
    this.#forEachMiniCart((element) => {
      element.clearRecommendations?.();
    });
  }

  /**
   * @param {object | undefined} payload
   */
  #onCartAdd(payload) {
    if (payload?.source === RECOMMEND_MINI_CART_SOURCE) return;
    if (payload?.source === GWP_EVENT_SOURCE || payload?.source === GWP_SYNC_SOURCE) return;

    const items = Array.isArray(payload?.items)
      ? payload.items.filter((item) => !item.properties?._gwp)
      : [];
    if (!items.length) return;

    this.#forEachMiniCart((element) => {
      element.refreshFromAdd?.(items);
    });
  }

  /**
   * @param {string} eventName
   * @param {object | undefined} payload
   */
  #onCartClear(eventName, payload) {
    if (payload?.source === RECOMMEND_MINI_CART_SOURCE) return;
    if (eventName !== 'cart:clear' && Number(payload?.item_count) !== 0) return;
    this.#clearRecommendations();
  }

  /**
   * @param {{ id?: string } | undefined} payload
   */
  #onDialogOpen(payload) {
    if (payload?.id !== 'cart-drawer') return;
    this.#restoreRecommendations();
  }

  #observeCartDrawer() {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;

    this.#drawerObserver = new MutationObserver(() => {
      if (cartDrawer.getAttribute('aria-hidden') === 'false') {
        this.#restoreRecommendations();
      }
    });
    this.#drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }
}

new RecommendCartDrawerSync();
